import { Inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IS_MICROFRONTEND } from '@client/mfe/tokens/mfe-tokens';

export interface MfeUser {
    id: number;
    email: string;
    first_name?: string;
    last_name?: string;
    [key: string]: unknown;
}

export interface MfeConfig {
    apiUrl?: string;
    apiBaseUrl?: string;
    accessToken?: string;
    [key: string]: unknown;
}

/**
 * Hard-required CC configs that must be present before the MFE may boot.
 * Soft/defaulted configs (organizationName, showTopNav, showSidebar, theme,
 * basePath/baseUrl) are intentionally NOT part of this list.
 */
export interface MfeRequiredConfigs {
    accessToken: string;
    apiUrl: string;
    organizationId: string;
    user: MfeUser;
}

/**
 * Error emitted when the CC-config readiness gate times out.
 * `missingConfigs` lists every hard-required config that was still absent
 * when the gate gave up waiting.
 */
export class MfeReadinessError extends Error {
    constructor (
        public readonly missingConfigs: string[],
        public readonly timeoutMs: number,
    ) {
        super(`MFE bootstrap timed out after ${timeoutMs}ms waiting for CC configs. Missing: ${missingConfigs.join(', ')}`);
        this.name = 'MfeReadinessError';
    }
}

/**
 * Normalizes a CC-delivered config object.
 *
 * The Command Center sends `config = { locale, apiBaseUrl, accessToken }` —
 * the base URL arrives under `apiBaseUrl`, but every downstream consumer
 * (OauthService.getApiPath, SessionService.getSelectedApi, the readiness
 * gate) expects the canonical `apiUrl` key. Map apiBaseUrl → apiUrl when
 * apiUrl is absent/empty so both the gate and consumers see one key.
 * `apiBaseUrl` itself is preserved for backward compatibility with any
 * consumer that already reads it.
 */
function normalizeMfeConfig (config: MfeConfig): MfeConfig {
    if (!config.apiUrl && config.apiBaseUrl) {
        return { ...config, apiUrl: config.apiBaseUrl };
    }
    return config;
}

/**
 * Returns the list of hard-required CC configs that are still null/empty.
 * An empty array means the MFE has everything it needs to boot safely.
 */
export function getMissingMfeConfigs (
    config: MfeConfig | null,
    organizationId: string | null,
    user: MfeUser | null,
): string[] {
    const missing: string[] = [];

    if (!config?.accessToken) {
        missing.push('config.accessToken');
    }
    // Accept either the canonical apiUrl or the raw CC apiBaseUrl so the gate
    // never reports apiUrl missing when the CC provided it under apiBaseUrl.
    if (!config?.apiUrl && !config?.apiBaseUrl) {
        missing.push('config.apiUrl');
    }
    if (!organizationId) {
        missing.push('organizationId');
    }
    if (!user) {
        missing.push('user');
    }

    return missing;
}

@Injectable({
    providedIn: 'root',
})
export class MfeDataService {
    public readonly user$: BehaviorSubject<MfeUser | null> = new BehaviorSubject<MfeUser | null>(null);
    public readonly config$: BehaviorSubject<MfeConfig | null> = new BehaviorSubject<MfeConfig | null>(null);
    public readonly basePath$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    public readonly theme$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    public readonly navigationPath$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    public readonly organizationId$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    public readonly organizationName$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    public readonly showSidebar$: BehaviorSubject<boolean>;
    public readonly showTopNav$: BehaviorSubject<boolean>;

    constructor (@Optional() @Inject(IS_MICROFRONTEND) isMicrofrontend: boolean | null) {
        const defaultSidebarVisibility = !isMicrofrontend;

        this.showSidebar$ = new BehaviorSubject<boolean>(defaultSidebarVisibility);
        this.showTopNav$ = new BehaviorSubject<boolean>(true);
    }

    setProperty (key: string, value: unknown): void {
        switch (key) {
            case 'user':
                this.user$.next(value != null ? value as MfeUser : null);
                break;
            case 'config':
                this.config$.next(value != null ? normalizeMfeConfig(value as MfeConfig) : null);
                break;
            case 'basePath':
            case 'baseUrl':
                this.basePath$.next(value != null ? value as string : null);
                break;
            case 'theme':
                this.theme$.next(value != null ? value as string : null);
                break;
            case 'navigationPath':
                this.navigationPath$.next(value != null ? value as string : null);
                break;
            case 'organizationId':
                this.organizationId$.next(value != null ? value as string : null);
                break;
            case 'organizationName':
                this.organizationName$.next(value != null ? value as string : null);
                break;
            case 'showSidebar':
                this.showSidebar$.next(value as boolean);
                break;
            case 'showTopNav':
                this.showTopNav$.next(value as boolean);
                break;
            default:
                break;
        }
    }

    hasUser (): boolean {
        return this.user$.getValue() != null;
    }

    getApiUrl (): string | null {
        return this.config$.getValue()?.apiUrl ?? null;
    }

    getAccessToken (): string | null {
        return this.config$.getValue()?.accessToken ?? null;
    }

    getOrganizationId (): string | null {
        return this.organizationId$.getValue();
    }
}
